import type {RefObject} from 'react';
import {useCallback, useEffect, useRef, useState} from 'react';
import type {QRSocialPlatform} from '@Libraries/qr-code/qr-social-platform.library';
import {getQRSocialPlatformIconFileName} from '@Libraries/qr-code/qr-social-platform.library';
import type {
  ScrollAffordanceState,
  QRCodePreviews,
  QRResolvedPlatformLogo,
  QRPreviewStyle,
  QRRasterInputs,
  QRRasterRequest,
} from '@Components/qr-code-generator/qr-code-generator.types';
import {
  ScrollAxis,
  QR_PREVIEW_DEBOUNCE_MS,
  QR_PREVIEW_SIZE_PX,
} from '@Components/qr-code-generator/qr-code-generator.types';
import {renderStyledQRCodeBlob} from '@Components/qr-code-generator/qr-code-generator.library';
import {blobToDataUrl} from '@Utils/file.util';
import useBackgroundClick from '@Hooks/useBackgroundClick';
import {ScreenBreakpoints} from '@Libraries/responsiveness-library';
import {useIsSmallerThanBreakpointWidth} from '@Hooks/useIsSmallerThanBreakpoint';

const ESCAPE_KEY = 'Escape';
const SCROLL_END_TOLERANCE_PX = 1;

export const useScrollAffordances = (scrollStepPx: number, axis: ScrollAxis = ScrollAxis.HORIZONTAL): ScrollAffordanceState => {
  const scrollableRef = useRef<HTMLDivElement>(null);
  const [canScrollBack, setCanScrollBack] = useState(false);
  const [canScrollForward, setCanScrollForward] = useState(false);
  const isVertical = axis === ScrollAxis.VERTICAL;

  const refreshScrollState = useCallback((): void => {
    const scrollable = scrollableRef.current;
    if (scrollable === null) {
      return;
    }
    const scrolled = isVertical ? scrollable.scrollTop : scrollable.scrollLeft;
    const visible = isVertical ? scrollable.clientHeight : scrollable.clientWidth;
    const total = isVertical ? scrollable.scrollHeight : scrollable.scrollWidth;
    setCanScrollBack(scrolled > SCROLL_END_TOLERANCE_PX);
    setCanScrollForward(scrolled + visible < total - SCROLL_END_TOLERANCE_PX);
  }, [isVertical]);

  useEffect((): (() => void) | undefined => {
    const scrollable = scrollableRef.current;
    if (scrollable === null) {
      return undefined;
    }
    refreshScrollState();

    let queuedFrame: number | null = null;
    const onScroll = (): void => {
      if (queuedFrame !== null) {
        return;
      }
      queuedFrame = window.requestAnimationFrame((): void => {
        queuedFrame = null;
        refreshScrollState();
      });
    };

    scrollable.addEventListener('scroll', onScroll, {passive: true});
    window.addEventListener('resize', onScroll);
    return (): void => {
      if (queuedFrame !== null) {
        window.cancelAnimationFrame(queuedFrame);
      }
      scrollable.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, [refreshScrollState]);

  const scrollBy = useCallback(
    (distancePx: number): void => {
      const step = isVertical ? {top: distancePx} : {left: distancePx};
      scrollableRef.current?.scrollBy({...step, behavior: 'smooth'});
    },
    [isVertical]
  );

  const scrollBack = useCallback((): void => {
    scrollBy(-scrollStepPx);
  }, [scrollBy, scrollStepPx]);

  const scrollForward = useCallback((): void => {
    scrollBy(scrollStepPx);
  }, [scrollBy, scrollStepPx]);

  return {scrollableRef, canScrollBack, canScrollForward, scrollBack, scrollForward};
};

const useDebouncedQRRasters = ({renderInputs, logoDataUrl, debounceMs}: QRRasterRequest): QRCodePreviews => {
  const [previewUrls, setPreviewUrls] = useState<(string | null)[]>([]);
  const [isRendering, setIsRendering] = useState(true);
  const displayedUrlsRef = useRef<string[]>([]);
  const renderInputsRef = useRef<QRRasterInputs[]>(renderInputs);
  renderInputsRef.current = renderInputs;
  const renderInputsKey = JSON.stringify(renderInputs);

  useEffect((): (() => void) => {
    let isLatestRender = true;
    setIsRendering(true);

    const timeoutId = window.setTimeout(() => {
      void (async (): Promise<void> => {
        try {
          const blobs = await Promise.all(
            renderInputsRef.current.map((inputs): Promise<Blob> => {
              return renderStyledQRCodeBlob({...inputs, logoDataUrl}, 'png');
            })
          );
          const renderedUrls = blobs.map((blob): string => {
            return URL.createObjectURL(blob);
          });
          if (!isLatestRender) {
            revokeObjectUrls(renderedUrls);
            return;
          }
          const replacedUrls = displayedUrlsRef.current;
          displayedUrlsRef.current = renderedUrls;
          setPreviewUrls(renderedUrls);
          revokeObjectUrls(replacedUrls);
        } catch (error) {
          window.PMW.log('QR preview render failed', error);
        } finally {
          if (isLatestRender) {
            setIsRendering(false);
          }
        }
      })();
    }, debounceMs);

    return (): void => {
      isLatestRender = false;
      window.clearTimeout(timeoutId);
    };
  }, [renderInputsKey, logoDataUrl, debounceMs]);

  useEffect(() => {
    return (): void => {
      revokeObjectUrls(displayedUrlsRef.current);
    };
  }, []);

  return {previewUrls, isRendering};
};

export const useQRCodePreviews = (payloads: string[], style: QRPreviewStyle): QRCodePreviews => {
  const {logoDataUrl, ...codeStyle} = style;
  return useDebouncedQRRasters({
    renderInputs: payloads.map((payload): QRRasterInputs => {
      return {payload, sizePx: QR_PREVIEW_SIZE_PX, ...codeStyle};
    }),
    logoDataUrl,
    debounceMs: QR_PREVIEW_DEBOUNCE_MS,
  });
};

const revokeObjectUrls = (objectUrls: string[]): void => {
  objectUrls.forEach((objectUrl): void => {
    URL.revokeObjectURL(objectUrl);
  });
};

export const useDismissOnEscape = (onDismiss: () => void, isActive = true): void => {
  useEffect((): (() => void) | undefined => {
    if (!isActive) {
      return undefined;
    }
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === ESCAPE_KEY) {
        onDismiss();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return (): void => {
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [onDismiss, isActive]);
};

export const useDismissOnOutsideInteraction = (isOpen: boolean, onDismiss: () => void): RefObject<HTMLDivElement | null> => {
  const containerRef = useRef<HTMLDivElement>(null);
  useBackgroundClick(containerRef, onDismiss, isOpen);
  useDismissOnEscape(onDismiss, isOpen);
  return containerRef;
};

const platformIconDataUrls = new Map<QRSocialPlatform, string>();

const fetchPlatformIconDataUrl = async (iconUrl: string, signal: AbortSignal): Promise<string> => {
  const response = await fetch(iconUrl, {signal});
  if (!response.ok) {
    throw new Error(`Platform icon request failed with status ${response.status}`);
  }
  return blobToDataUrl(await response.blob());
};

export const useSocialPlatformLogo = (platform: QRSocialPlatform | null, iconBaseUrl: string): string | null => {
  const [resolvedLogo, setResolvedLogo] = useState<QRResolvedPlatformLogo | null>(null);

  useEffect((): (() => void) | undefined => {
    if (platform === null || iconBaseUrl === '') {
      setResolvedLogo(null);
      return undefined;
    }

    const iconFileName = getQRSocialPlatformIconFileName(platform);
    if (iconFileName === null) {
      setResolvedLogo(null);
      return undefined;
    }

    const cachedDataUrl = platformIconDataUrls.get(platform);
    if (cachedDataUrl !== undefined) {
      setResolvedLogo({platform, dataUrl: cachedDataUrl});
      return undefined;
    }

    setResolvedLogo(null);
    const abortController = new AbortController();

    void (async (): Promise<void> => {
      try {
        const dataUrl = await fetchPlatformIconDataUrl(`${iconBaseUrl}${iconFileName}`, abortController.signal);
        platformIconDataUrls.set(platform, dataUrl);
        if (!abortController.signal.aborted) {
          setResolvedLogo({platform, dataUrl});
        }
      } catch (error) {
        if (!abortController.signal.aborted) {
          window.PMW.log(`Could not load the ${platform} icon for the QR code`, error);
        }
      }
    })();

    return (): void => {
      abortController.abort();
    };
  }, [platform, iconBaseUrl]);

  /**
   * Matched against the platform asked for, not just returned. The state is reset inside the effect above, which runs after the render that
   * changed the platform — so on that render it still holds the previous platform's icon, and handing that back would let a caller treat a
   * logo the visitor has not been given as already resolved.
   */
  return resolvedLogo?.platform === platform ? resolvedLogo.dataUrl : null;
};

export const useIsCompactQRTool = (): boolean => {
  return useIsSmallerThanBreakpointWidth(ScreenBreakpoints.QR_TOOL_COMPACT_BREAKPOINT);
};

/** Narrow enough that the actions bar cannot seat three labelled buttons, so the secondary two keep only their icons. */
export const useIsIconOnlyQRActions = (): boolean => {
  return useIsSmallerThanBreakpointWidth(ScreenBreakpoints.QR_TOOL_ICON_ONLY_ACTIONS_BREAKPOINT);
};
